import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ru } from 'date-fns/locale';
registerLocale('ru', ru);

const DISH_CATEGORIES = ['салат', 'первое', 'второе', 'гарнир', 'напиток'];
const STEP_DATE_SELECTION = 'dateSelection';
const STEP_CART_PREVIEW = 'cartPreview';

const UserPanel = () => {
  const { user, authFetch, isTelegramApp } = useUser();
  const {
    cart,
    balance,
    increase,
    decrease,
    favorites,
    toggleFavorite,
    deliveryDate,
    setDeliveryDate,
    needsCutlery,
    setNeedsCutlery,
    needsBread,
    setNeedsBread,
    breadType,
    setBreadType
  } = useCart();
  const navigate = useNavigate();

  const [allDishes, setAllDishes] = useState([]);
  const [dishesForSelectedDate, setDishesForSelectedDate] = useState([]);

  const [currentStep, setCurrentStep] = useState(STEP_DATE_SELECTION);
  const [selectedDateState, setSelectedDateState] = useState(null);
  const [orderSelections, setOrderSelections] = useState({});
  
  const [currentCategoryDisplayDishes, setCurrentCategoryDisplayDishes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);

  if (user?.role === 'user' && !isTelegramApp) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-gray-900 text-white text-center p-6">
        <h1 className="text-2xl font-bold mb-4">⛔ Веб-доступ недоступен</h1>
        <p className="mb-4">Для оформления заказа воспользуйтесь нашим Telegram-ботом:</p>
        <a
          href="https://t.me/Bon_appetitt_bot"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded shadow"
        >
          Открыть бота в Telegram
        </a>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Доброе утро';
    if (hour >= 12 && hour < 17) return 'Добрый день';
    if (hour >= 17 && hour < 23) return 'Добрый вечер';
    return 'Доброй ночи';
  };

  const userName = user?.firstName || user?.username || 'друг';
  const itemCountInCart = cart.reduce((total, item) => total + item.quantity, 0);
  const getQuantity = (id) => cart.find(item => item.id === id)?.quantity || 0;
  const isFavorite = (id) => favorites.includes(id);

  useEffect(() => {
    const fetchAllDishes = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch('/dishes');
        setAllDishes(res || []);
      } catch (err) {
        console.error('Ошибка загрузки блюд:', err);
        setError('Не удалось загрузить меню.');
      } finally {
        setLoading(false);
      }
    };

    const fetchUnavailableDates = async () => {
      try {
        const dates = await authFetch('/dates/unavailable');
        const parsedDates = dates.map(d => {
          const [year, month, day] = d.split('-').map(Number);
          return new Date(year, month - 1, day);
        });
        setUnavailableDates(parsedDates || []);
      } catch (err) {
        console.error('Ошибка загрузки недоступных дат:', err);
      }
    };

    fetchAllDishes();
    fetchUnavailableDates();
  }, [authFetch]);

  useEffect(() => {
    if (selectedDateState && allDishes.length > 0) {
      const dateString = selectedDateState.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const filtered = allDishes.filter(dish =>
        Array.isArray(dish.days) && dish.days.includes(dateString)
      );
      setDishesForSelectedDate(filtered);
    } else {
      setDishesForSelectedDate([]);
    }
  }, [selectedDateState, allDishes]);

  useEffect(() => {
    if (DISH_CATEGORIES.includes(currentStep) && dishesForSelectedDate.length > 0) {
      const categoryKey = currentStep;
      const filtered = dishesForSelectedDate.filter(d => d.dishClass === categoryKey);
      setCurrentCategoryDisplayDishes(filtered);
    } else {
      setCurrentCategoryDisplayDishes([]);
    }
  }, [currentStep, dishesForSelectedDate]);

  const handleDateSelectAndProceed = (date) => {
    if (date) {
      setSelectedDateState(date);
      setOrderSelections({});
      setCurrentStep(DISH_CATEGORIES[0]);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      setDeliveryDate(`${year}-${month}-${day}`);
    }
  };
  
  const handleToggleDishSelection = (dish) => {
    setOrderSelections(prev => {
      const currentCategorySelections = prev[currentStep] ? [...prev[currentStep]] : [];
      const existingDishIndex = currentCategorySelections.findIndex(d => d.id === dish.id);

      if (existingDishIndex > -1) {
        // Убираем блюдо из выбранных
        currentCategorySelections.splice(existingDishIndex, 1);
        
        // Если убираем второе блюдо с гарниром, проверяем гарниры
        if (currentStep === 'второе' && dish.hasGarnir === true) {
          // Пересчитываем максимальное количество гарниров после удаления блюда
          const remainingSecondCoursesWithGarnir = currentCategorySelections.filter(d => d.hasGarnir === true);
          const maxGarnirCount = remainingSecondCoursesWithGarnir.length;
          
          // Если гарниров больше чем можно, убираем лишние
          const currentGarnirSelections = prev['гарнир'] || [];
          if (currentGarnirSelections.length > maxGarnirCount) {
            const updatedState = { ...prev, [currentStep]: currentCategorySelections };
            updatedState['гарнир'] = currentGarnirSelections.slice(0, maxGarnirCount);
            return updatedState;
          }
        }
      } else {
        // Пытаемся добавить блюдо
        
        // Если это гарнир, проверяем ограничение
        if (currentStep === 'гарнир') {
          // Считаем количество вторых блюд с hasGarnir: true
          const secondCourseSelections = prev['второе'] || [];
          const secondCoursesWithGarnir = secondCourseSelections.filter(d => d.hasGarnir === true);
          const maxGarnirCount = secondCoursesWithGarnir.length;
          
          // Считаем текущее количество выбранных гарниров
          const currentGarnirCount = currentCategorySelections.length;
          
          // Если уже выбрано максимальное количество гарниров
          if (currentGarnirCount >= maxGarnirCount) {
            alert(`Нельзя выбрать больше ${maxGarnirCount} гарнир${maxGarnirCount === 1 ? 'а' : 'ов'}. Количество гарниров не может превышать количество вторых блюд с гарниром.`);
            return prev; // Не изменяем состояние
          }
        }
        
        currentCategorySelections.push(dish);
      }
      return { ...prev, [currentStep]: currentCategorySelections };
    });
  };

  const handleNextStep = () => {
    const currentCategoryIndex = DISH_CATEGORIES.indexOf(currentStep);

    if (currentStep === 'второе') {
      const secondCourseSelections = orderSelections['второе'];
      const hasGarnir = secondCourseSelections?.some(d => d.hasGarnir === true);
    
      if (secondCourseSelections && secondCourseSelections.length > 0 && hasGarnir) {
        setCurrentStep('гарнир');
      } else {
        setCurrentStep('напиток');
      }
    } else if (currentCategoryIndex < DISH_CATEGORIES.length - 1) {
      setCurrentStep(DISH_CATEGORIES[currentCategoryIndex + 1]);
    } else {
      setCurrentStep(STEP_CART_PREVIEW);
    }
  };

  const handlePreviousStep = () => {
    const currentCategoryIndex = DISH_CATEGORIES.indexOf(currentStep);

    if (currentStep === STEP_CART_PREVIEW || (currentStep === DISH_CATEGORIES[0] && currentCategoryIndex === 0) ) {
      if (setBreadType) setBreadType(null);
    }

    if (currentStep === 'напиток') {
      // Если перед напитком был пропущен гарнир (т.к. не выбрали второе), то возвращаемся на второе
      // Иначе, если гарнир был, то на гарнир.
      // Проверяем, есть ли что-то в orderSelections для 'второе'. Если да, значит гарнир был предложен.
      // Однако, пользователь мог выбрать второе, потом гарнир, потом вернуться и убрать второе.
      // Более надежно - посмотреть, есть ли категория 'гарнир' в DISH_CATEGORIES перед 'напиток'
      const гарнирIndex = DISH_CATEGORIES.indexOf('гарнир');
      const напитокIndex = DISH_CATEGORIES.indexOf('напиток');
      const второеIndex = DISH_CATEGORIES.indexOf('второе');

      // Если предыдущий шаг по списку - гарнир, и он не равен шагу "второе"
      // (на случай если "гарнир" вдруг переместят до "второго" - защита от некорректного порядка)
      if (гарнирIndex === напитокIndex - 1 && гарнирIndex > второеIndex) {
        const secondCourseSelections = orderSelections['второе'];
        const hasGarnir = secondCourseSelections?.some(d => d.hasGarnir === true);
     
        if (secondCourseSelections && secondCourseSelections.length > 0 && hasGarnir) {
           setCurrentStep('гарнир');
           return;
        }
     }
     
      // В противном случае (если гарнир не был предложен или второе не выбрано) - возвращаемся на 'второе'
      setCurrentStep('второе');

    } else if (currentStep === 'гарнир') {
      setCurrentStep('второе');
    } else if (currentCategoryIndex > 0) {
      setCurrentStep(DISH_CATEGORIES[currentCategoryIndex - 1]);
    } else if (currentStep === DISH_CATEGORIES[0] || currentStep === STEP_CART_PREVIEW) { // Если текущий шаг - первый в категориях или превью корзины
      setCurrentStep(STEP_DATE_SELECTION);
      setSelectedDateState(null);
      setDishesForSelectedDate([]);
      setCurrentCategoryDisplayDishes([]);
      setOrderSelections({});
      setDeliveryDate(null);
      if (setBreadType) setBreadType(null);
    }
  };
  
  const handleGoToCart = () => {
    console.log("Переход в корзину с выбором:", orderSelections);
    const allSelectedItems = Object.values(orderSelections).flat();
    
    allSelectedItems.forEach(dish => {
      increase(dish); 
    });

    if (selectedDateState) {
    }
    navigate('/my-order');
  };

  if (loading && currentStep === STEP_DATE_SELECTION) {
    return <div className="text-white p-4 text-center text-lg">Загрузка меню...</div>;
  }
  if (error && allDishes.length === 0) {
      return <div className="text-red-400 p-4 text-center">{error}</div>;
  }

  return (
    <div className="relative p-4 pb-20 max-w-screen-md mx-auto">
      <div className="absolute top-4 right-4 z-50">
        <button
          onClick={() => navigate('/my-order')}
          className="relative bg-blue-800 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg"
          aria-label="Корзина"
        >
          <span className="text-xl">🛒</span>
          {itemCountInCart > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {itemCountInCart}
            </span>
          )}
        </button>
      </div>

      {currentStep === STEP_DATE_SELECTION && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 mt-2 bg-gray-800 rounded-xl p-5 text-center shadow-xl border border-gray-700"
        >
          <p className="text-white text-lg font-bold">{getGreeting()}, {userName}! 👋</p>
          <p className="text-gray-300 text-sm mt-2">Пожалуйста, выберите дату для вашего заказа 👇</p>
          <p className="text-sm text-gray-400 mt-1">
             Баланс: <span className={`font-semibold ${balance <= 500 ? 'text-red-400' : 'text-green-400'}`}>{balance} ₽</span>
          </p>

          <div className="flex justify-center mt-4">
              <DatePicker
                selected={selectedDateState}
                onChange={handleDateSelectAndProceed}
                inline
                locale="ru"
                minDate={new Date()}
                excludeDates={unavailableDates}
                calendarClassName="bg-gray-900 border-gray-700 text-white"
                dayClassName={() => "text-white hover:bg-indigo-700 rounded-full"}
                todayButton="Сегодня"
              />
          </div>
          <button
              onClick={() => navigate('/orders-history')}
              className="mt-4 bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-lg shadow transition"
          >
              📜 История заказов
          </button>
        </motion.div>
      )}

      {DISH_CATEGORIES.includes(currentStep) && (
        <motion.div 
          key={currentStep}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="bg-gray-800 p-6 rounded-lg shadow-xl mt-6"
        >
          {selectedDateState && (
            <p className="text-center text-teal-400 mb-4 text-md font-medium">
              Доставка на: {selectedDateState.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <h2 className="text-2xl font-semibold text-white mb-3 text-center capitalize">
            {currentStep}
          </h2>
          
          {currentStep === 'гарнир' && (
            <div className="mb-4 text-center">
              {(() => {
                const secondCourseSelections = orderSelections['второе'] || [];
                const secondCoursesWithGarnir = secondCourseSelections.filter(d => d.hasGarnir === true);
                const maxGarnirCount = secondCoursesWithGarnir.length;
                const currentGarnirCount = (orderSelections['гарнир'] || []).length;
                
                if (maxGarnirCount === 0) {
                  return <p className="text-yellow-400 text-sm">Вы не выбрали вторые блюда с гарниром</p>;
                } else {
                  return (
                    <p className="text-teal-400 text-sm">
                      Можно выбрать до {maxGarnirCount} гарнир{maxGarnirCount === 1 ? 'а' : 'ов'} 
                      {currentGarnirCount > 0 && ` (выбрано: ${currentGarnirCount})`}
                    </p>
                  );
                }
              })()}
            </div>
          )}
          {error && <p className="text-red-400 text-center mb-3">{error}</p>}
          
          {loading && dishesForSelectedDate.length === 0 && <p className="text-gray-400 text-center">Загрузка блюд на выбранную дату...</p>}

          {!loading && dishesForSelectedDate.length === 0 && selectedDateState && (
            <div className="text-center text-yellow-400 py-4">
                <p>На дату {selectedDateState.toLocaleDateString('ru-RU')} блюда не найдены или еще не загружены.</p>
                <button 
                    onClick={handlePreviousStep}
                    className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded shadow"
                >
                    Выбрать другую дату
                </button>
            </div>
          )}

          {dishesForSelectedDate.length > 0 && currentCategoryDisplayDishes.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {currentCategoryDisplayDishes.map((dish) => {
                const isSelected = orderSelections[currentStep]?.find(d => d.id === dish.id);
                
                // Проверяем, можно ли выбрать этот гарнир
                let isGarnirDisabled = false;
                if (currentStep === 'гарнир' && !isSelected) {
                  const secondCourseSelections = orderSelections['второе'] || [];
                  const secondCoursesWithGarnir = secondCourseSelections.filter(d => d.hasGarnir === true);
                  const maxGarnirCount = secondCoursesWithGarnir.length;
                  const currentGarnirCount = (orderSelections['гарнир'] || []).length;
                  isGarnirDisabled = currentGarnirCount >= maxGarnirCount;
                }
                
                return (
                  <motion.div
                    key={dish.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => !isGarnirDisabled && handleToggleDishSelection(dish)}
                    className={`rounded-xl overflow-hidden shadow-lg transform transition-all duration-200 ease-out
                                ${isGarnirDisabled 
                                  ? 'opacity-50 cursor-not-allowed bg-gray-600' 
                                  : isSelected 
                                    ? 'border-4 border-teal-500 bg-teal-900 cursor-pointer hover:scale-[1.03]' 
                                    : 'bg-gray-700 hover:bg-gray-600 cursor-pointer hover:scale-[1.03]'}`}
                  >
                    {dish.imageUrl && (
                      <img src={dish.imageUrl} alt={dish.name} className="w-full h-32 object-cover" />
                    )}
                    <div className="p-3">
                      <h3 className="text-base font-semibold text-white mb-1">{dish.name}</h3>
                      {user?.role !== 'user' && (
  <p className="text-sm text-teal-400 font-medium mb-2">{dish.price} ₽</p>
)}

                      {isSelected && (
                        <p className="text-sm text-green-400 font-semibold">✓ Выбрано</p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            dishesForSelectedDate.length > 0 && !loading && (
                 <p className="text-center text-gray-400 italic py-4">
                    Для категории "{currentStep}" на выбранную дату блюд нет.
                 </p>
            )
          )}
        </motion.div>
      )}
      
      {currentStep === STEP_CART_PREVIEW && (
        <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800 p-6 rounded-lg shadow-xl mt-6"
        >
          {selectedDateState && (
            <p className="text-center text-teal-400 mb-4 text-md font-medium">
              Дата доставки: {selectedDateState.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <h2 className="text-2xl font-semibold text-white mb-5 text-center">Ваш заказ:</h2>
          {Object.values(orderSelections).flat().length > 0 ? (
            <ul className="space-y-3 mb-6">
              {DISH_CATEGORIES.map(category =>
                orderSelections[category] && orderSelections[category].length > 0 && (
                  <li key={category}>
                    <p className="text-lg capitalize font-medium text-teal-400">{category}:</p>
                    <ul className="list-disc list-inside ml-5 text-gray-300 text-sm">
                      {orderSelections[category].map(dish => (
                        <li key={dish.id}>{dish.name}</li>

                      ))}
                    </ul>
                  </li>
                )
              )}
            </ul>
          ) : (
            <p className="text-center text-gray-400 italic py-4">Вы пока ничего не выбрали.</p>
          )}

          <div className="space-y-4 mt-6 border-t border-gray-700 pt-6">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Нужны приборы?</span>
              <label htmlFor="needsCutleryToggle" className="flex items-center cursor-pointer">
                <div className="relative">
                  <input type="checkbox" id="needsCutleryToggle" className="sr-only" checked={needsCutlery} onChange={() => setNeedsCutlery(!needsCutlery)} />
                  <div className={`block w-10 h-6 rounded-full ${needsCutlery ? 'bg-teal-600' : 'bg-gray-600'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${needsCutlery ? 'translate-x-full' : ''}`}></div>
                </div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Нужен хлеб?</span>
              <label htmlFor="needsBreadToggle" className="flex items-center cursor-pointer">
                <div className="relative">
                  <input 
                    type="checkbox" 
                    id="needsBreadToggle" 
                    className="sr-only" 
                    checked={needsBread} 
                    onChange={() => {
                      const newNeedsBread = !needsBread;
                      setNeedsBread(newNeedsBread);
                      if (!newNeedsBread) {
                        setBreadType(null);
                      }
                    }} 
                  />
                  <div className={`block w-10 h-6 rounded-full ${needsBread ? 'bg-teal-600' : 'bg-gray-600'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${needsBread ? 'translate-x-full' : ''}`}></div>
                </div>
              </label>
            </div>
            {needsBread && (
              <div className="pl-4 mt-3 space-y-3 border-l border-gray-700 ml-2">
                <p className="text-sm text-gray-400 mb-1">Выберите тип хлеба:</p>
                <div className="flex flex-col space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer p-2 rounded-md hover:bg-gray-700 transition-colors">
                    <input 
                      type="radio" 
                      name="breadType" 
                      value="white" 
                      checked={breadType === 'white'} 
                      onChange={(e) => setBreadType(e.target.value)}
                      className="form-radio h-4 w-4 text-teal-500 bg-gray-700 border-gray-600 focus:ring-teal-500" 
                    />
                    <span className="text-gray-200">Белый</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer p-2 rounded-md hover:bg-gray-700 transition-colors">
                    <input 
                      type="radio" 
                      name="breadType" 
                      value="black" 
                      checked={breadType === 'black'} 
                      onChange={(e) => setBreadType(e.target.value)}
                      className="form-radio h-4 w-4 text-teal-500 bg-gray-700 border-gray-600 focus:ring-teal-500"
                    />
                    <span className="text-gray-200">Черный</span>
                  </label>
                </div>
              </div>
            )}
          </div>

        </motion.div>
      )}

      {currentStep !== STEP_DATE_SELECTION && (
         <div className="fixed bottom-0 left-0 right-0 bg-gray-900 bg-opacity-90 backdrop-blur-sm p-4 flex justify-around items-center border-t border-gray-700 shadow-2xl md:max-w-screen-md md:mx-auto md:rounded-t-xl">
            <button
                onClick={handlePreviousStep}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white font-medium rounded-lg shadow transition-colors"
            >
                Назад
            </button>
            {currentStep !== STEP_CART_PREVIEW ? (
                <button
                    onClick={handleNextStep}
                    disabled={!selectedDateState || (DISH_CATEGORIES.includes(currentStep) && dishesForSelectedDate.length === 0)}
                    className="px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg shadow transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                    {DISH_CATEGORIES.indexOf(currentStep) === DISH_CATEGORIES.length - 1 ? 'К заказу' : 'Далее'}
                </button>
            ) : (
                <button
                    onClick={handleGoToCart}
                    disabled={Object.values(orderSelections).flat().length === 0}
                    className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-bold rounded-lg shadow-lg transition-colors text-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
                >
                    В корзину
                </button>
            )}
        </div>
      )}
    </div>
  );
};

export default UserPanel;
